import { useState, useEffect} from 'react';
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { TableRow, TableCell, Tooltip, Paper, Stack, Button, Grid, ToggleButtonGroup, ToggleButton, Typography, Menu, MenuItem, Box, FormControl, InputLabel, Select, Checkbox, ListItemText, OutlinedInput, setRef } from '@mui/material'
import DBQuoteOfTheDay from './DashboardComponents/DBQuoteOfTheDay'
import DBPortfolioHighLevel from './DashboardComponents/DBPortfolioHighLevel'
import DBPortfolioDetailed from './DashboardComponents/DBPortfolioDetailed'
import DBSectorPerformance from './DashboardComponents/DBSectorPerformance';
import DBGainersAndLosers from './DashboardComponents/DBGainersAndLosers';
import TickerSearch from './TickerSearch.js'
import axios from 'axios';

import {useAtomValue} from 'jotai';
import { emailAtom } from './Atoms.js';

import { BarChart, Bar, Cell, YAxis } from "recharts";
import { marketNamesWithPINX as marketNames, regionNames, sectorNames, marketCapValues } from "./Constants/constants";
import { useNavigate } from 'react-router-dom';

import { performanceColorCellFromValue } from "./utils/PerformanceColourScale.js"

const WEBAPI = process.env.REACT_APP_WEB_API;

const TIMEFRAMES = {
  '3Y': 'pricechg3years',
  '1Y': 'pricechg1years',
  '6M': 'pricechg6months',
  '3M': 'pricechg3months',
  '1M': 'pricechg1months',
  '1W': 'pricechg1week',
  '1D': 'pricechg1day'
}

const buttonStyle = {
  textTransform: 'none',
    "&.Mui-selected": {
      backgroundColor: "#002060",
      color: "white",
    },
    "&.Mui-selected:hover": {
      backgroundColor: "#002060",
    }
}

function Dashboard() {
  const [market, setMarket] = useState(localStorage.getItem("DASH_Market") || "CAN");
  const [regions, setRegions] = useState(localStorage.getItem("DASH_Regions") || []);
  const [marketCapMin, setMarketCapMin] = useState(localStorage.getItem("DASH_MarketCapMin") || 0);
  const [marketCapMax, setMarketCapMax] = useState(localStorage.getItem("DASH_MarketCapMax") || 10000000);
  const [regionDisplay, setRegionDisplay] = useState("none");
  const [refreshEnabled, setRefreshEnabled] = useState(false);
  const [companiesData, setCompaniesData] = useState([]);
  const [companies, setCompaines] = useState([])
  const [timeframe, setTimeframe] = useState(localStorage.getItem("DASH_Timeframe") || '1M')
  const [searchEnabled, setSearchEnabled] = useState(true)
  const [order, setOrder] = useState(localStorage.getItem("DASH_Order") || 'asc')
  const [top, setTop] = useState(localStorage.getItem("DASH_Top") || 5)
  const navigate = useNavigate()
 
  const userEmail = useAtomValue(emailAtom);
  
  const adminEmails = ["mkacur@fsavaluation.com", "ken@afxsoftware.com", "kenc@team-eagle.ca"];
  const isAdmin = adminEmails.includes(userEmail);

  useEffect(() => {
    const fetchResults = async () => {
      setSearchEnabled(false)
      setRefreshEnabled(false)

    // const localMarket = localStorage.getItem("DASH_Market") || "CAN";
    // const localRegions = localStorage.getItem("DASH_Regions") || [];
    // const localMarketCapMin = localStorage.getItem("DASH_MarketCapMin") || 0;
    // const localMarketCapMax = localStorage.getItem("DASH_MarketCapMax") || 10000000;

    localStorage.setItem("DASH_Market", market);
    localStorage.setItem("DASH_Regions", regions);
    localStorage.setItem("DASH_MarketCapMin", marketCapMin);
    localStorage.setItem("DASH_MarketCapMax", marketCapMax);
    localStorage.setItem("DASH_Timeframe", timeframe);
    localStorage.setItem("DASH_Order", order);
    localStorage.setItem("DASH_Top", top);

      const searchResults = await getSearchResults()
      setCompaniesData(searchResults)

      //const trimmedResults = searchResults

      setCompaines(searchResults)
    };

    if(searchEnabled) fetchResults()
  }, [searchEnabled])


const getSearchResults = async () => {
  // extract sector codes
  const sectorCodes = sectorNames.map(s => s.value);
  // fetch each sector in parallel
  const allResults = await Promise.all(
    sectorCodes.map(async (code) => {
      const payload = {
        market,
        regions,
        sector: code,
        marketCapMin,
        marketCapMax,
      };

      try {
        const res = await axios.post(`${WEBAPI}/quicksector/results`, payload);

        // process charts exactly like your existing logic
        res.data.forEach((row) => {
            const charts = ["chart_cfirrEx", "chart_basicEBITDA", "chart_dividendYield", "chart_revenue"];
  
            charts.forEach(chartName => {
              if (!row[chartName]) {
                row[chartName] = [];
                return;
              }

              row[chartName] = row[chartName].split(",").map(Number).map((item, idx) => {
                let color;
                if (chartName === "chart_cfirrEx") color = item > 0 ? "green" : "red";
                else if (chartName === "chart_basicEBITDA" || chartName === "chart_dividendYield") color = item > 0 ? "cornflowerblue" : "red";
                else if (chartName === "chart_revenue") color = idx <= 5 ? (item > 0 ? "cornflowerblue" : "red") : (item > 0 ? "lightblue" : "lightred");
                return { y: item, color };
              });
            });
          
          // attach sector for grouping later
          row.sector = code;
        });

        return res.data;
      } catch (err) {
        console.error("Error fetching sector", code, err);
        return [];
      }
    })
  );

  // flatten array of arrays into a single array
  //console.log(allResults)
  const combinedResults = allResults.flat();
  if(order === 'asc') {
    combinedResults.sort((a, b) => b[TIMEFRAMES[timeframe]] - a[TIMEFRAMES[timeframe]])
  } else {
    combinedResults.sort((a, b) => a[TIMEFRAMES[timeframe]] - b[TIMEFRAMES[timeframe]])
  }
  //console.log("Combined results:", combinedResults);
  return combinedResults;
};

  const handleMarketChange = (event) => {
    const newMarket = event.target.value;
    //console.log(newMarket);
    setMarket(newMarket);

    if (newMarket === "PNX") {
      setRegionDisplay("block");
    } else {
      setRegionDisplay("none");
    }
    setRegions([]);
    localStorage.setItem("DASH_Regions", []);

    localStorage.setItem("DASH_Market", newMarket);

    setRefreshEnabled(true);
  };

  const handleRegionChange = (event) => {
    const newRegions = event.target.value;
    //console.log(newRegions);

    setRegions(newRegions);
    localStorage.setItem("DASH_Regions", newRegions);

    setRefreshEnabled(true);
  };

  const handleMarketCapMinChange = (event) => {
    const newMarketCapMin = event.target.value;
    //console.log(newMarketCapMin);

    setMarketCapMin(newMarketCapMin);
    localStorage.setItem("DASH_MarketCapMin", newMarketCapMin);

    setRefreshEnabled(true);
  };

  const handleMarketCapMaxChange = (event) => {
    const newMarketCapMax = event.target.value;
    //console.log(newMarketCapMax);

    setMarketCapMax(newMarketCapMax);
    localStorage.setItem("DASH_MarketCapMax", newMarketCapMax);

    setRefreshEnabled(true);
  };

  const getMCapMinValues = () => {
    const mCapMinValues = marketCapValues.filter((mCap) => mCap.mCapMin === true && mCap.value < marketCapMax);
    return mCapMinValues;
  };

  const getMCapMaxValues = () => {
    const mCapMaxValues = marketCapValues.filter((mCap) => mCap.mCapMax === true && mCap.value > marketCapMin);
    return mCapMaxValues;
  };

  const handleTimeframeChange = (event) => {
    const newTimeframe = event.target.value
    localStorage.setItem("DASH_Timeframe", newTimeframe);
    setTimeframe(newTimeframe)
    if(order === 'asc') {
      companiesData.sort((a, b) => b[TIMEFRAMES[newTimeframe]] - a[TIMEFRAMES[newTimeframe]])
    } else {
      companiesData.sort((a, b) => a[TIMEFRAMES[newTimeframe]] - b[TIMEFRAMES[newTimeframe]])
    }
    setCompaines(companiesData)
    //setRefreshEnabled(true);
  }

  const handleOrder = (event) => {
    const newOrder = event.target.value
    if(newOrder !== order) {
      setCompaines(companiesData.reverse())
    }
    localStorage.setItem("DASH_Order", newOrder);
    setOrder(newOrder)
  }

  const handleTop = (event) => {
    //console.log(Number(event.target.value))
    const newTop = Number(event.target.value)
    localStorage.setItem("DASH_Top", newTop);
    setTop(newTop)
  }

  const handleTicker = (newTicker) => {
    if (!newTicker) return;
    console.log(newTicker)
    navigate(`/${newTicker.toLowerCase()}`); 
  };

  //console.log(companies)
  return (
    <Grid container sx={{ p: { xs: 2, sm: 4 } }}>
      <Grid item xs={12} sx={{ mb: 1 }}>         
        <Paper sx={{ p: { xs: 1, sm: 2 } }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={2}>
              <Box
                sx={{
                  width: "100%",
                  "& .MuiAutocomplete-root": { width: "100% !important" },
                  "& .MuiInputBase-root": {
                    width: "100% !important",
                    fontSize: "0.75rem",
                  },
                  "& .MuiInputLabel-root": { fontSize: "0.75rem" },
                  "& .MuiAutocomplete-option": { fontSize: "0.75rem" },
                  "& .MuiAutocomplete-popupIndicator": { display: "none" },
                }}
              >
                <TickerSearch
                  handleAddTicker={handleTicker}
                  fromCompanyDetail={true}
                />
              </Box>
            </Grid>

            <Grid item xs={12} md={10}>
              <DBQuoteOfTheDay />
            </Grid>
          </Grid>
        </Paper>
      </Grid>

      <Grid item xs={12} sx={{ position: 'sticky', top: 0, zIndex: 1000, backgroundColor: 'white' }}>
      <Paper sx={{ my: 1, p: { xs: 1, sm: 2 },}}>
        <Grid container spacing={1} alignItems="center" wrap="wrap">
          {/* <Grid item xs={12} sm="auto">
            <FormControl sx={{ m: 0, width: 150 }} size="small">
              <InputLabel>Timeframe</InputLabel>
              <Select
                value={timeframe}
                onChange={handleTimeframeChange}
                input={<OutlinedInput label="Timeframe" />}
              >
                {Object.keys(TIMEFRAMES).map((t) => (
                  <MenuItem key={t} value={t}>{t}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid> */}

          <Grid item xs={12} md="auto">
            <FormControl sx={{ m: 0, width: 110 }} size="small">
              <InputLabel>Market</InputLabel>
              <Select
                value={market}
                onChange={handleMarketChange}
                input={<OutlinedInput label="Market" />}
              >
                {marketNames.map((m) => (
                  <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md="auto" sx={{ display: regionDisplay }}>
            <FormControl sx={{ m: 0, minWidth: 200 }} size="small">
              <InputLabel>Regions</InputLabel>
              <Select
                multiple
                value={regions}
                onChange={handleRegionChange}
                input={<OutlinedInput label="Regions" />}
                renderValue={(selected) => selected.join(", ")}
              >
                {regionNames.map((region) => (
                  <MenuItem key={region.value} value={region.value}>
                    <Checkbox checked={regions.indexOf(region.value) > -1} />
                    <ListItemText primary={`${region.label} - ${region.value}`} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={6} md="auto">
            <FormControl sx={{ m: 0, width: 150 }} size="small">
              <InputLabel>Market Cap Min</InputLabel>
              <Select
                value={marketCapMin}
                onChange={handleMarketCapMinChange}
                input={<OutlinedInput label="Market Cap Min" />}
              >
                {getMCapMinValues().map((cap) => (
                  <MenuItem key={cap.value} value={cap.value}>{cap.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={6} md="auto">
            <FormControl sx={{ m: 0, width: 150 }} size="small">
              <InputLabel>Market Cap Max</InputLabel>
              <Select
                value={marketCapMax}
                onChange={handleMarketCapMaxChange}
                input={<OutlinedInput label="Market Cap Max" />}
              >
                {getMCapMaxValues().map((cap) => (
                  <MenuItem key={cap.value} value={cap.value}>{cap.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md="auto">
            <Button
              variant="contained"
              fullWidth
              size="small"
              onClick={() => setSearchEnabled(true)}
              disabled={!refreshEnabled}
            >
              Refresh
            </Button>
          </Grid>

          <Grid item xs={6} md="auto">

            <ToggleButtonGroup
              size="small"
              exclusive
              aria-label="Small sizes"
              value={timeframe}
            >
              <ToggleButton value='3Y' onClick={handleTimeframeChange} sx={buttonStyle}>3Y</ToggleButton>
              <ToggleButton value='1Y' onClick={handleTimeframeChange} sx={buttonStyle}>1Y</ToggleButton>
              <ToggleButton value='6M' onClick={handleTimeframeChange} sx={buttonStyle}>6M</ToggleButton>
              <ToggleButton value='3M' onClick={handleTimeframeChange} sx={buttonStyle}>3M</ToggleButton>
              <ToggleButton value='1M' onClick={handleTimeframeChange} sx={buttonStyle}>1M</ToggleButton>
              <ToggleButton value='1W' onClick={handleTimeframeChange} sx={buttonStyle}>1W</ToggleButton>
              <ToggleButton value='1D' onClick={handleTimeframeChange} sx={buttonStyle}>1D</ToggleButton>

            </ToggleButtonGroup>

            <ToggleButtonGroup
              size="small"
              exclusive
              aria-label="Small sizes"
              sx={{ml: 1}}
              value={order}
            >
              <ToggleButton value='asc' onClick={handleOrder} sx={buttonStyle}>Gainers</ToggleButton>
              <ToggleButton value='desc' onClick={handleOrder} sx={buttonStyle}>Losers</ToggleButton>
            </ToggleButtonGroup>

            <ToggleButtonGroup
              size="small"
              exclusive
              aria-label="Small sizes"
              sx={{ml: 1}}
              value={String(top)}
            >
              <ToggleButton value='5' onClick={handleTop} sx={buttonStyle}>Top 5</ToggleButton>
              <ToggleButton value='10' onClick={handleTop} sx={buttonStyle}>Top 10</ToggleButton>
              <ToggleButton value='20' onClick={handleTop} sx={buttonStyle}>Top 20</ToggleButton>

            </ToggleButtonGroup>
          </Grid>

          {/* <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              flex: 1, // take remaining space
              maxWidth: 250,
              mt: { xs: 2, sm: 0 },
              ml: "auto"
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                borderBottom: "1px solid currentColor",
                mb: 1,
              }}
            >
              <Typography variant="body2">Stock Market %</Typography>
              <Typography variant="body2" sx={{ pr: 1 }}>24 Qtrs</Typography>
            </Box>

            <Box display="flex" alignItems="center" justifyContent="space-between">
              {["3Y", "1Y", "6M", "3M", "1M", "1W", "1D"].map((label) => (
                <Box key={label} sx={{ flex: 0.25, textAlign: "left" }}>
                  <Typography fontSize="small" 
                    fontWeight={label === timeframe ? 'bold' : 'none'}
                    color={label === timeframe ? '#002060' : 'black'}
                    >
                      {label}
                    </Typography>
                </Box>
              ))}
              <Box sx={{ flex: 1, textAlign: "right" }}>
                  <Typography fontSize="small" fontWeight="bold" color="green">CFIRR</Typography>
              </Box>
            </Box>
          </Box> */}
        </Grid>
        </Paper>
      </Grid>

      {/* {isAdmin && ( */}
      <Grid container spacing={2}>
        {sectorNames.map((sector) => (
          <Grid item xs={12} lg={6} xl={4} key={sector.value}>        
            <Paper sx={{ px: 2, py: 1 }}>
              <Typography key={sector.value} sx={{ fontWeight: 'bold', color: "#4e95d9", mb: 1 }} >
                {sector.label.split(" - ")[0]}
              </Typography>
              
              <TableRow sx={{ paddingTop: 1, color: "black" }} >
                  <TableCell component="th" scope="row" padding="none" align="left" sx={{ fontFamily: "ArchivoNarrow-Regular", fontSize: "0.75rem", minWidth: 120 }}>
                    Company
                  </TableCell>
                  <TableCell component="th" scope="row" padding="none" align="center" sx={{ fontFamily: "ArchivoNarrow-Regular", fontSize: "0.75rem", minWidth: 40, color: timeframe === '3Y' ? '#002060' : 'black', fontWeight: timeframe === '3Y' ? 'bold' : 'none', borderBottom: timeframe === '3Y' ? '3px solid #002060' : 'none' }}>
                    3Y
                  </TableCell>
                  <TableCell component="th" scope="row" padding="none" align="center" sx={{ fontFamily: "ArchivoNarrow-Regular", fontSize: "0.75rem", minWidth: 40, color: timeframe === '1Y' ? '#002060' : 'black', fontWeight: timeframe === '1Y' ? 'bold' : 'none', borderBottom: timeframe === '1Y' ? '3px solid #002060' : 'none' }}>
                    1Y
                  </TableCell>
                  <TableCell component="th" scope="row" padding="none" align="center" sx={{ fontFamily: "ArchivoNarrow-Regular", fontSize: "0.75rem", minWidth: 40, color: timeframe === '6M' ? '#002060' : 'black', fontWeight: timeframe === '6M' ? 'bold' : 'none', borderBottom: timeframe === '6M' ? '3px solid #002060' : 'none' }}>
                    6M
                  </TableCell>
                  <TableCell component="th" scope="row" padding="none" align="center" sx={{ fontFamily: "ArchivoNarrow-Regular", fontSize: "0.75rem", minWidth: 40, color: timeframe === '3M' ? '#002060' : 'black', fontWeight: timeframe === '3M' ? 'bold' : 'none', borderBottom: timeframe === '3M' ? '3px solid #002060' : 'none' }}>
                    3M
                  </TableCell>
                  <TableCell component="th" scope="row" padding="none" align="center" sx={{ fontFamily: "ArchivoNarrow-Regular", fontSize: "0.75rem", minWidth: 40, color: timeframe === '1M' ? '#002060' : 'black', fontWeight: timeframe === '1M' ? 'bold' : 'none', borderBottom: timeframe === '1M' ? '3px solid #002060' : 'none' }}>
                    1M
                  </TableCell>
                  <TableCell component="th" scope="row" padding="none" align="center" sx={{ fontFamily: "ArchivoNarrow-Regular", fontSize: "0.75rem", minWidth: 40, color: timeframe === '1W' ? '#002060' : 'black', fontWeight: timeframe === '1W' ? 'bold' : 'none', borderBottom: timeframe === '1W' ? '3px solid #002060' : 'none' }}>
                    1W
                  </TableCell>
                  <TableCell component="th" scope="row" padding="none" align="center" sx={{ fontFamily: "ArchivoNarrow-Regular", fontSize: "0.75rem", minWidth: 40, color: timeframe === '1D' ? '#002060' : 'black', fontWeight: timeframe === '1D' ? 'bold' : 'none', borderBottom: timeframe === '1D' ? '3px solid #002060' : 'none' }}>
                    1D
                  </TableCell>
                  <TableCell component="th" scope="row" padding="none" align='center' sx={{ fontFamily: "ArchivoNarrow-Regular", fontSize: "0.75rem", width: 100, minWidth: 100, maxWidth: 100,  }}>
                    CFIRR 24 Qtrs
                  </TableCell>
              </TableRow>

              {companies.filter(row => row.sector === sector.value).slice(0, top).map((row) => (                
                <TableRow sx={{ paddingTop: 1, color: "black" }} >
                    <TableCell padding="none" sx={{ cursor: "pointer", "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.15)" },  fontFamily: "ArchivoNarrow-Regular", fontSize: "0.75rem", width: "120px", minWidth: "90px", whiteSpace: "nowrap" }} onClick={() => navigate(`/${row.ticker.toLowerCase()}`)}>
                      <img src={`https://flagcdn.com/w20/${row.domicile_country_flag?.toLowerCase()}.png`} width="20" alt="flag" style={{ marginRight: 4 }} />
                      {row.ticker}
                      <br/>
                      {row.company}
                    </TableCell>

                    <TableCell component="th" scope="row" padding="none" align="right" sx={{ fontFamily: "ArchivoNarrow-Regular", fontSize: "0.75rem", backgroundColor: performanceColorCellFromValue(row.pricechg3years), borderStyle: "hidden", minWidth: 40 }}>
                      {isNaN(row.pricechg3years) ? "" : (Math.round(row.pricechg3years * 10) / 10).toFixed(1)}%
                    </TableCell>
                    <TableCell component="th" scope="row" padding="none" align="right" sx={{ fontFamily: "ArchivoNarrow-Regular", fontSize: "0.75rem", backgroundColor: performanceColorCellFromValue(row.pricechg1years), borderStyle: "hidden" , minWidth: 40 }}>
                      {isNaN(row.pricechg1years) ? "" : (Math.round(row.pricechg1years * 10) / 10).toFixed(1)}%
                    </TableCell>
                    <TableCell component="th" scope="row" padding="none" align="right" sx={{ fontFamily: "ArchivoNarrow-Regular", fontSize: "0.75rem", backgroundColor: performanceColorCellFromValue(row.pricechg6months), borderStyle: "hidden", minWidth: 40 }}>
                      {isNaN(row.pricechg6months) ? "" : (Math.round(row.pricechg6months * 10) / 10).toFixed(1)}%
                    </TableCell>
                    <TableCell component="th" scope="row" padding="none" align="right" sx={{ fontFamily: "ArchivoNarrow-Regular", fontSize: "0.75rem", backgroundColor: performanceColorCellFromValue(row.pricechg3months), borderStyle: "hidden", minWidth: 40 }}>
                      {isNaN(row.pricechg3months) ? "" : (Math.round(row.pricechg3months * 10) / 10).toFixed(1)}%
                    </TableCell>
                    <TableCell component="th" scope="row" padding="none" align="right" sx={{ fontFamily: "ArchivoNarrow-Regular", fontSize: "0.75rem", backgroundColor: performanceColorCellFromValue(row.pricechg1months), borderStyle: "hidden", minWidth: 40 }}>
                      {isNaN(row.pricechg1months) ? "" : (Math.round(row.pricechg1months * 10) / 10).toFixed(1)}%
                    </TableCell>
                    <TableCell component="th" scope="row" padding="none" align="right" sx={{ fontFamily: "ArchivoNarrow-Regular", fontSize: "0.75rem", backgroundColor: performanceColorCellFromValue(row.pricechg1week), borderStyle: "hidden", minWidth: 40 }}>
                      {isNaN(row.pricechg1week) ? "" : (Math.round(row.pricechg1week * 10) / 10).toFixed(1)}%
                    </TableCell>
                    <TableCell component="th" scope="row" padding="none" align="right" sx={{ fontFamily: "ArchivoNarrow-Regular", fontSize: "0.75rem", backgroundColor: performanceColorCellFromValue(row.pricechg1day), borderStyle: "hidden", minWidth: 40 }}>
                      {isNaN(row.pricechg1day) ? "" : (Math.round(row.pricechg1day * 10) / 10).toFixed(1)}%
                    </TableCell>

                    <TableCell component="th" scope="row" padding="none" sx={{ width: 100, minWidth: 100, maxWidth: 100,  }}>
                      <Box display="flex" justifyContent="center" alignItems="center"  height={30}>
                        <BarChart width={100} height={30} data={row.chart_cfirrEx} margin={{ top: 0, right: 0, left: 2, bottom: 0 }}>
                          <YAxis type="number" domain={[-10, 30]} hide="true" allowDataOverflow={true} />
                          <Bar dataKey="y" color="color">
                            {row.chart_cfirrEx.map((entry, index) => (
                              <Cell key={`chart_cfirr_cell-${index}`} fill={entry.color} />
                            ))}
                          </Bar>
                        </BarChart>
                      </Box>
                    </TableCell>

                </TableRow>
              ))}
            </Paper>
          </Grid>
        ))}
      </Grid>
      {/* )} */}


    </Grid>);
}

export default Dashboard;
